<template>
  <div class="playerholder3" :class="{ 'is-online': isOnline, 'has-incoming-stream': incomingStream, 'disable-border-radius': component.disable_border_radius }">
    <div class="playerholder3-player-container">
      <!-- WowzaWebRTCPlayer: displayed when online, handles its own video playback -->
      <WowzaWebRTCPlayer
        v-if="isOnline && streamDescriptor"
        ref="webrtcPlayer"
        :stream-descriptor="streamDescriptor"
        class="playerholder3-video"
      />

      <!-- État offline: affiche signal_off_text -->
      <div v-if="state === 'offline'" class="playerholder3-info playerholder3-offline">
        <span>
          <span class="playerholder3-led failed"></span>
          {{ signalOffText || 'Signal Off...' }}
        </span>
      </div>

      <!-- État initializing: affiche text avec progress bar -->
      <div v-if="state === 'initializing'" class="playerholder3-info playerholder3-initializing">
        <span>{{ displayText || 'Processing incoming stream...' }}</span>
        <div class="playerholder3-progress"></div>
      </div>

      <!-- Anchor pour maintenir la hauteur quand pas online -->
      <div v-if="!isOnline" class="playerholder3-anchor"></div>
    </div>

    <!-- Instructions affichées en dessous quand offline ou initializing -->
    <FFadeTransitionOutIn>
      <div class="playerholder3-instructions">
        <VuePlaceholder v-if="component.instructions_component" :component="component.instructions_component" />
      </div>
    </FFadeTransitionOutIn>
  </div>
</template>

<script>
import WowzaWebRTCPlayer from './WowzaWebRTCPlayer.vue';

export default {
  components: {
    WowzaWebRTCPlayer,
  },
  props: ["component", "handler"],
  computed: {
    incomingStream() {
      return this.component?.incoming_stream || false;
    },
    streamInitialized() {
      return this.component?.stream_initialized || false;
    },
    signalOffText() {
      return this.component?.signal_off_text || 'Signal Off...';
    },
    displayText() {
      return this.component?.text || 'Processing incoming stream...';
    },
    streamDescriptor() {
      const descriptor = this.component?.stream_descriptor;
      if (!descriptor) return null;
      
      return {
        host: descriptor.host,
        http_port: descriptor.http_port || descriptor.port,
        application: descriptor.application,
        stream_name: descriptor.stream_name,
        stream_play_key: descriptor.stream_play_key,
      };
    },
    state() {
      if (!this.streamInitialized && !this.incomingStream) {
        return 'offline';
      } else if (this.incomingStream && !this.streamInitialized) {
        return 'initializing';
      } else {
        return 'online';
      }
    },
    isOnline() {
      return this.state === 'online';
    },
  },
};
</script>

<style lang="scss">
.playerholder3 {
  position: relative;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  gap: 20px;

  aspect-ratio: 16 / 9;

  .playerholder3-player-container {
    position: relative;
    width: 100%;
    transition: all 250ms ease-in-out;
    flex-shrink: 0;
    border-radius: 8px;
    overflow: hidden;

    background-color: var(--black);
    
    // Hauteur par défaut : 80px
    height: 80px;
  }

  &.disable-border-radius .playerholder3-player-container {
    border-radius: 0;
  }

  // Quand incoming_stream est vrai : 50% de la hauteur
  &.has-incoming-stream:not(.is-online) {
    .playerholder3-player-container {
      height: 50%;
    }
  }

  // Quand stream_initialized est vrai : 100% de la hauteur
  &.is-online {
    .playerholder3-player-container {
      height: 100%;
      width: 100%;
    }
  }

  // Styles pour les états offline et initializing
  .playerholder3-info {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: white;
    font-weight: 600;
    z-index: 1;
  }

  .playerholder3-offline {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .playerholder3-initializing {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    max-width: 100%;
  }

  .playerholder3-progress {
    background-image: linear-gradient(122deg, #8cca89, #3cbca6 40%, #168198);
    background-repeat: repeat;
    background-size: 200% 200%;
    height: 4px;
    width: 20em;
    max-width: 90%;
    margin: 1em;
    animation-name: wanimation_progress;
    animation-duration: 2s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }

  @keyframes wanimation_progress {
    0% {
      background-position: 0%;
    }
    100% {
      background-position: 200%;
    }
  }

  .playerholder3-led {
    width: 0.7em;
    height: 0.7em;
    display: inline-block;
    margin-right: 0.5em;
    border-radius: 50%;
    background-color: #ff4444;
  }

  .playerholder3-anchor {
    display: flex;
    width: 100%;
    height: 100%;
    background-color: #000;
    text-align: center;
    color: #fff;
  }

  .playerholder3-video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: #000;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
  }

  .playerholder3-instructions {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 0; // Nécessaire pour que flex: 1 fonctionne correctement avec overflow

    > * {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    // S'assurer que f-form prend toute la hauteur
    .f-form {
      height: 100%;
      min-height: 0;
      flex: 1;
      display: flex;
      flex-direction: column;

      // S'assurer que FSexyScroll prend toute la hauteur disponible
      .f-sexy-scroll {
        flex: 1;
        min-height: 0;
        height: 0; // Nécessaire pour que flex fonctionne avec overflow
      }
    }
  }

  // Masquer les instructions quand online
  &.is-online .playerholder3-instructions {
    display: none;
  }
}

// Transitions pour le fade
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

